import { Injectable } from '@angular/core'
import { select, Store } from '@ngrx/store'
import { selectAppId } from '../../modules/config/config.selectors'
import { CHARACTER_LIMIT_COMMENTS, CHARACTER_LIMIT_COMMENTS_RAP } from '../../modules/dashboard/calls.constants'
import { ContactSchema } from '../../modules/dashboard/calls.model'
import { AAACallData, AAACallDataAdapted, AAACallStatus, CallDetailsComment, CallDetailsResponse, CallStatus, CallType, ContactType, PhoneType } from '../../modules/dashboard/calls.types'
import { IssueType } from '../../modules/issue/issue.types'
import { DEFAULT_SPECIAL_ASSISTANCE, SPECIAL_ASSISTANCE_LABELS, SpecialAssistanceOptions } from '../../modules/location/location.types'
import { RapService } from '../../modules/rap/rap.service'
import { unmaskNumericField } from '../../modules/ui/ui.utils'
import { AAAStore } from '../../store/root-reducer'
import { Adapter, DestinationType } from '../types'
import { DEFAULT_APP_ID } from '../utils/cookies'
import { toTitleCase } from '../utils/titlecase'
import { VehicleAdapter } from "./VehicleAdapter"

const SPECIAL_ASSISTANCE_KEYS = Object.keys(DEFAULT_SPECIAL_ASSISTANCE) as Array<keyof SpecialAssistanceOptions>
const SPECIAL_ASSISTANCE_LABEL_VALUES = Object.values(SPECIAL_ASSISTANCE_LABELS)

const extractDriverDirectionsFromComments = (comments?: CallDetailsComment[]): string | undefined => {
  if (!comments || comments.length === 0) {
    return undefined
  }

  const specialAssistanceComment = comments.find(comment =>
    SPECIAL_ASSISTANCE_LABEL_VALUES.some(label => comment.text.includes(label)) ||
    SPECIAL_ASSISTANCE_KEYS.some(key => comment.text.includes(key))
  )

  return specialAssistanceComment?.text
}

@Injectable({
  providedIn: 'root',
})
export class AAACallDataAdapter implements Adapter<AAACallDataAdapted> {

  private _storeAppId: string

  constructor(
    private store$: Store<AAAStore>,
    private rapService: RapService,
    private vehicleAdapter: VehicleAdapter,
  ) {
    this.store$.pipe(select(selectAppId)).subscribe(appId => this._storeAppId = appId)
  }

  adapt(data: AAACallData): AAACallDataAdapted {
    const isRapUser = this.rapService.isRapUser()
    const appId = this._storeAppId;
    const callType = isRapUser ? CallType.RAP : CallType.MEMBER
    const characterLimitComments = isRapUser
      ? CHARACTER_LIMIT_COMMENTS_RAP
      : CHARACTER_LIMIT_COMMENTS;

    const comments = [
      `DRRWeb 2.0`,
      appId !== DEFAULT_APP_ID ? appId : '',
      data.channel || '',
      data.comments || '',
    ]
      .join(' ')
      .replace(/  +/g, ' ')
      .trim()
      .substring(0, characterLimitComments);

    const driverDirections = data.breakdownLocation?.driverDirections?.substring(0, characterLimitComments)

    const breakDownStAddress = `${ data?.breakdownLocation?.street || '' }`.trim();
    const towingStAddress = `${ data?.towing?.streetName || '' }`.trim();
    const countryFormatted = data.towing?.country === 'USA' ? 'US' : data.towing?.country
    const towId = (data.towing?.aarId || data.towing?.id) ? `${data.towing.aarId || data.towing.id}` : null

    const issueType = data.service?.issueType
    const towType = (
      issueType === IssueType.FUEL_ELECTRIC
        ? DestinationType.EV_STATION
        : isRapUser
          ? data.towing?.foiType || DestinationType.DEALER
          : data.towing?.aarId
            ? DestinationType.AAR
            : DestinationType.TOW_DESTINATION
    )

    let contact = {
      firstName: data.contactInfo.firstName,
      lastName: data.contactInfo.lastName,
      contactType: ContactType.CUSTOMER,
      ...(data.contactInfo.languagePreference
        ? { languagePreference: data.contactInfo.languagePreference }
        : {}),
      phones: [
        ...(data.contactInfo.phone ? [{
          phoneNumber: unmaskNumericField(data.contactInfo.phone),
          preferred: true,
          phoneType: PhoneType.CELLULAR,
          smsOptIn: data.contactInfo.enableSMS,
        }] : [])
      ],
    };

    try {
      ContactSchema.validateSync(contact);
    } catch (error) {
      contact = null;
      console.warn('Invalid contact data:', error);
    }

    const agent = {
      ...(data.agentFirstName ? { firstName: data.agentFirstName } : {}),
      ...(data.agentLastName ? { lastName: data.agentLastName } : {}),
    }

    const appointmentInfo = {
      ...(data.appointment ? {
        appointmentStartTime: data.appointment.slot.startTime.toISOString(),
        appointmentEndTime: data.appointment.slot.endTime.toISOString(),
      } : {}),
    }

    return {
      callType: callType,
      ...(Object.keys(agent).length > 0 && { agent }),
      ...(contact && { contact }),
      ...( data.vehicle.model ? { vehicle: this.vehicleAdapter.adaptCallVehicle(data.vehicle) } : null ),
      ...( data.service?.issueType ? {
        service: {
          ...( data?.numberOfPassengers ? { numberOfPassengers: data.numberOfPassengers } : null),
          collision: issueType === IssueType.ACCIDENT,
          ...appointmentInfo,
          ...data.service,
          ...( data.channel ? { channel: data.channel } : null),
        },
      } : null ),
      breakdownLocation: {
        ...data.breakdownLocation,
        ...( driverDirections ? { driverDirections } : null ),
        streetNumber: data.breakdownLocation.streetNumber,
        ...( data.breakdownLocation?.locationCode ? { locationCode: data.breakdownLocation?.locationCode } : null ),
        streetName: breakDownStAddress,
        city: toTitleCase(data.breakdownLocation.city),
        state: data.breakdownLocation.state,
        postalCode: data.breakdownLocation.zip,
        country: data.breakdownLocation.country,
        latitude: data.breakdownLocation.latitude,
        longitude: data.breakdownLocation.longitude,
      },
      ...( data?.towing
        ? {
          towDestination: {
            ...(towId ? { id: data.towing.serviceProviderCode || towId } : null),
            ...(towId ? { name: data.towing?.facility || data.towing?.name } : null),
            streetNumber: data.towing?.streetNumber,
            streetName: towingStAddress,
            city: toTitleCase(data.towing.city),
            state: data.towing.state,
            latitude: Number(data.towing.latitude || data.towing.lat),
            longitude: Number(data.towing.longitude || data.towing.lng),
            postalCode: data.towing.zip || data.towing.postalCode,
            country: countryFormatted,
            emailAddress: data.towing?.emailAddress,
            type: towType,
          }
        }
        : null ),
      ...(isRapUser && data?.eligibility ? { eligibility: data.eligibility } : null ),
      comments: [
        {
          text: comments,
        },
      ],
    };
  }

  adaptCallDetailsResponse(response: CallDetailsResponse): AAACallStatus {
    const driverDirections = response.breakdownLocation?.driverDirections
      || extractDriverDirectionsFromComments(response.comments)

    return {
      callKey: response.callKey,
      status: this.adaptCallStatus(response.service.status),
      pta: response.service.pta,
      pacesetterCode: response.service.pacesetterCode,
      numberOfPassengers: response.service.numberOfPassengers,
      vehicle: {
        ...response.vehicle,
      },
      breakdownLocation: {
        ...response.breakdownLocation,
        ...(driverDirections && { driverDirections }),
      },
      ...(response.towDestination && {
        towDestination: {
          ...response.towDestination,
          name: response.towDestination.location || response.towDestination.name,
        },
      }),
      driverData: {
        eta: response.service.eta,
      },
      ...(response.serviceTracking && {
        tracking: {
          provider: response.serviceTracking.system,
          url: response.serviceTracking.link
        }
      }),
      ...(response.service.appointmentStartTime && response.service.appointmentEndTime && {
        appointment: {
          slot: {
            startTime: response.service.appointmentStartTime,
            endTime: response.service.appointmentEndTime,
          },
        },
      }),
      issueType: response.service.issueType,
    };
  }

  adaptCallStatus(status: string): CallStatus {
    switch (status) {
      case 'OL':
      case 'OS':
        return CallStatus.ARRIVED
      case 'ER':
        return CallStatus.EN_ROUTE
      case 'UT':
      case 'TW':
        return CallStatus.TOWING
      case 'TC':
      case 'CP':
      case 'HD':
      case 'CL':
        return CallStatus.COMPLETED
      case 'CA':
      case 'XX':
        return CallStatus.CANCELLED
      default:
        return CallStatus.REQUEST_RECEIVED
    }
  }
}
